import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getUserService } from '@src/container';
import type { UserDTO } from '@src/application/users/dto/user.dto';
import type { UpdateProfileInputDTO } from '@src/application/users/dto/user-input.dto';

/**
 * Hook: useUpdateProfile
 * Updates authenticated user's profile information
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for updating profile
 * 
 * @example
 * ```typescript
 * const { mutate: updateProfile, isPending, error } = useUpdateProfile({
 *     onSuccess: (updatedUser) => {
 *         console.log('Profile updated:', updatedUser);
 *     }
 * });
 * 
 * // Usage
 * updateProfile({
 *     first_name: 'Jane',
 *     last_name: 'Doe',
 *     middle_name: 'M'
 * });
 * ```
 */
export function useUpdateProfile(
    options?: Omit<
        UseMutationOptions<UserDTO, Error, UpdateProfileInputDTO>,
        'mutationFn'
    >
) {
    const userService = getUserService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: UpdateProfileInputDTO) => {
            return await userService.updateProfile(data);
        },
        onSuccess: async (updatedUser) => {
            // Update the current user query
            queryClient.setQueryData(['auth', 'current-user'], updatedUser);

            // Invalidate users list in case admin is viewing it
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
        ...options,
    });
}
